import React from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { WarningAmberRounded as WarningIcon } from '@mui/icons-material';

// There was no error boundary anywhere, so any throw during render took the
// whole app to a white page with nothing on it — no message, no way back,
// and (because the sidebar went too) no way to reach another screen. This
// was not hypothetical: `new mapboxgl.Map()` throws outright on a machine
// with no WebGL, and the map used to BE the screen you landed on after
// signing in.
//
// One boundary inside the shell, so the chrome survives and only the page
// is replaced.

interface State { error: Error | null }

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('Screen crashed:', error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <Box sx={{ p: 3, maxWidth: 640, mx: 'auto' }}>
        <Stack alignItems="center" gap={1.5} sx={{ textAlign: 'center', py: 8 }}>
          <Box
            sx={{
              width: 44, height: 44, borderRadius: '50%', display: 'grid',
              placeItems: 'center', bgcolor: 'error.light', color: 'error.dark',
            }}
          >
            <WarningIcon />
          </Box>
          <Typography variant="h5">This screen did not load</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
            Something went wrong rendering this page. The rest of the app still
            works — pick another section from the sidebar, or try again.
          </Typography>
          <Typography
            variant="caption"
            sx={{
              mt: 1, px: 1.5, py: 1, maxWidth: '100%', borderRadius: 2,
              bgcolor: 'grey.100', color: 'text.secondary',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              wordBreak: 'break-word',
            }}
          >
            {error.message}
          </Typography>
          <Button variant="outlined" sx={{ mt: 1.5 }} onClick={() => this.setState({ error: null })}>
            Try again
          </Button>
        </Stack>
      </Box>
    );
  }
}

export default ErrorBoundary;
